import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Calendar,
  Clock,
  MapPin,
  BookOpen,
  User,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { AuthContext } from "../../contexts/AuthContext";
import { getMyStudentProfile } from "../../services/students";
import { getTeachers } from "../../services/teachers";

const StudentSchedule: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;

  const {
    data: studentData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["student-schedule"],
    queryFn: () => getMyStudentProfile(),
    enabled: !!user?.id,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl border border-red-200/60 rounded-2xl p-6 shadow-lg">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-red-900 font-semibold text-lg">
                Error Loading Schedule
              </h3>
              <p className="text-red-700 mt-2">
                Unable to load your schedule information. Please try refreshing
                the page.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const student = studentData?.data;

  // Prevent e-course students from accessing regular student pages
  if (student && student.studentType === "ecourse") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-orange-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Access Restricted
          </h2>
          <p className="text-gray-600 mb-4">
            Class schedule is not available for e-course students.
          </p>
          <a
            href="/student/ecourse-dashboard"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to E-Learning Dashboard
          </a>
        </div>
      </div>
    );
  }
  const courses = student?.coursesInfo || [];
  const schedule = student?.schedule;

  // Get the actual schedule periods
  let schedulePeriods = null;
  if (Array.isArray(schedule) && schedule.length > 0) {
    schedulePeriods = schedule[0]?.periods;
  } else if (schedule?.periods) {
    schedulePeriods = schedule.periods;
  }

  // Transform backend schedule data to match frontend format
  const transformScheduleData = (periods: any[]) => {
    if (!periods || !Array.isArray(periods)) return [];

    const daysMap: { [key: string]: any[] } = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    };

    // Group periods by day
    periods.forEach((period: any) => {
      if (daysMap[period.day.toLowerCase()]) {
        daysMap[period.day.toLowerCase()].push({
          time: `${period.startTime} - ${period.endTime}`,
          course: period.subjectName,
          room: period.room || "TBA",
          type: "Lecture", // Default type
        });
      }
    });

    // Convert to required format
    return Object.entries(daysMap)
      .filter(([_, classes]) => classes.length > 0)
      .map(([day, classes]) => ({
        day: day.charAt(0).toUpperCase() + day.slice(1),
        classes: classes.sort((a, b) => a.time.localeCompare(b.time)),
      }));
  };

  const weeklySchedule = schedulePeriods
    ? transformScheduleData(schedulePeriods)
    : [];

  // Get current day
  const currentDay = new Date().toLocaleDateString("en-US", {
    weekday: "long",
  });

  const todaySchedule = weeklySchedule.find(
    (daySchedule: any) => daySchedule.day === currentDay
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-3 sm:p-4 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            My Schedule
          </h1>
          <p className="text-slate-600 mt-1 text-sm sm:text-base">
            View your class schedule and timetable
          </p>
        </div>

        {/* Today's Schedule */}
        <div className="mb-6 lg:mb-8">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-4 lg:p-6 hover:shadow-xl transition-all duration-300">
            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center mr-3 shadow-md">
                <Calendar className="h-5 w-5 text-white flex-shrink-0" />
              </div>
              <span className="truncate">Today's Classes ({currentDay})</span>
            </h2>

            {todaySchedule && todaySchedule.classes.length > 0 ? (
              <div className="space-y-3 lg:space-y-4">
                {todaySchedule.classes.map((classItem: any, index: number) => (
                  <div
                    key={index}
                    className="p-4 lg:p-5 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200/60 rounded-xl hover:shadow-lg transition-all duration-300"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                        <div className="flex items-center bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2 rounded-lg shadow-md w-fit">
                          <Clock className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span className="font-semibold text-sm sm:text-base">
                            {classItem.time}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-bold text-slate-900 text-base sm:text-lg truncate">
                            {classItem.course}
                          </h3>
                          <div className="flex flex-col sm:flex-row sm:items-center text-xs sm:text-sm text-slate-600 mt-2 space-y-1 sm:space-y-0">
                            <div className="flex items-center bg-white px-3 py-1 rounded-lg shadow-sm w-fit sm:ml-2">
                              <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" />
                              <span>{classItem.room}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end">
                        <span
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold shadow-md ${
                            classItem.type === "Lab"
                              ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                              : classItem.type === "Practical"
                              ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                              : "bg-gradient-to-r from-blue-500 to-indigo-500 text-white"
                          }`}
                        >
                          {classItem.type}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <CheckCircle className="h-10 w-10 text-white" />
                </div>
                <p className="text-slate-600 text-sm sm:text-base font-medium">
                  No classes scheduled for today!
                </p>
                <p className="text-slate-500 text-sm mt-1">
                  Enjoy your free time 🎉
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Weekly Schedule */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-4 lg:p-6 hover:shadow-xl transition-all duration-300">
          <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mr-3 shadow-md">
              <BookOpen className="h-5 w-5 text-white flex-shrink-0" />
            </div>
            Weekly Schedule
          </h2>

          <div className="space-y-6 lg:space-y-8">
            {weeklySchedule.length > 0 ? (
              weeklySchedule.map((daySchedule: any, dayIndex: number) => (
                <div
                  key={dayIndex}
                  className="relative pl-6 border-l-4 border-gradient"
                  style={{
                    borderLeftColor:
                      daySchedule.day === currentDay
                        ? "rgb(59, 130, 246)"
                        : "rgb(148, 163, 184)",
                  }}
                >
                  <div className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-slate-900 mb-4 flex items-center">
                    {daySchedule.day}
                    {daySchedule.day === currentDay && (
                      <span className="ml-3 px-3 py-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs font-semibold rounded-lg shadow-md">
                        Today
                      </span>
                    )}
                  </h3>
                  <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {daySchedule.classes.map(
                      (classItem: any, classIndex: number) => (
                        <div
                          key={classIndex}
                          className="p-4 bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-semibold bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-3 py-1.5 rounded-lg shadow-md">
                              {classItem.time}
                            </span>
                            <span
                              className={`px-2.5 py-1 rounded-lg text-xs font-semibold shadow-md ${
                                classItem.type === "Lab"
                                  ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                                  : classItem.type === "Practical"
                                  ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                                  : "bg-gradient-to-r from-blue-500 to-indigo-500 text-white"
                              }`}
                            >
                              {classItem.type}
                            </span>
                          </div>
                          <h4 className="font-bold text-slate-900 mb-3 text-base">
                            {classItem.course}
                          </h4>
                          <div className="space-y-2 text-sm text-slate-600">
                            <div className="flex items-center bg-white px-3 py-1.5 rounded-lg shadow-sm">
                              <MapPin className="h-3.5 w-3.5 mr-2 text-green-600" />
                              <span>{classItem.room}</span>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <AlertCircle className="h-10 w-10 text-white" />
                </div>
                <p className="text-slate-900 font-semibold text-lg">
                  No schedule available
                </p>
                <p className="text-slate-600 mt-2">
                  Please contact your administrator.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Course Information */}
        {courses.length > 0 && (
          <div className="mt-8 bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-xl transition-all duration-300">
            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center mr-3 shadow-md">
                <BookOpen className="h-5 w-5 text-white" />
              </div>
              Enrolled Courses
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {courses.map((course: any) => (
                <div
                  key={course._id}
                  className="p-5 bg-gradient-to-br from-slate-50 to-indigo-50 border border-slate-200 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-slate-900 text-base">
                      {course.name}({course.level})
                    </h3>
                    <span className="text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-3 py-1 rounded-lg shadow-md">
                      {course.code}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2">
                    {course.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentSchedule;
